import React, { FC, MouseEvent, useCallback, useMemo } from 'react';
import { Switch, Tooltip } from 'antd';
import { Text } from 'components/text/Text';
import { colors } from 'styles/colors';
import { WarningOutlined } from '@ant-design/icons';
import { DELETE, EDIT } from 'assets/icons/tutoring-institute';
import { RowWrapper } from '../common';
import { OfferResponse } from 'logic/api-models/api-models';
import { roundToXDecimals } from 'logic/utils/number';
import { navigationSlice } from 'logic/store/navigation/navigation.slice';
import { generatePath } from 'react-router-dom';
import { AppRoute } from 'app/app.route.const';
import { useDispatch, useSelector } from 'react-redux';
import { instituteSlice } from 'logic/store/institute/institute.slice';
import { translate, translationKeys } from 'logic/translations/translations.service';
import styled from 'styled-components';
import {
  getAreOffersInvisible,
  getHasUnpaidInvoicesFromPreviousSubscription,
  getIsBillingInfoProvided,
  getIsTogglingOffersEnabled,
  getSubscriptionStatus,
  paymentSlice,
} from 'logic/store/payment';
import { getOfferRoutePath } from 'logic/store/navigation/navigation.logic';

const PointerText = styled(Text)`
  cursor: pointer;
`;

const SwitcherWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WarningStyled = styled(WarningOutlined)`
  margin-right: 4px;
  margin-top: 4px;
  font-size: 20px;
  color: ${colors.red};
`;

export const ListItem: FC<OfferResponse> = (offer) => {
  const dispatch = useDispatch();
  const {
    appearedOnSearch = 0,
    isPublished,
    detailsDisplayed = 0,
    _id,
    location,
    tutoringSchool,
    averageRating = 0,
    totalRatings = 0,
  } = offer;

  const isBillingInfoProvided = useSelector(getIsBillingInfoProvided);
  const areOffersInvisible = useSelector(getAreOffersInvisible);
  const subscriptionStatus = useSelector(getSubscriptionStatus);
  const isTogglingOffersDisabled = !useSelector(getIsTogglingOffersEnabled);
  const hasUnpaidInvoicesFromPreviousSubscription = useSelector(
    getHasUnpaidInvoicesFromPreviousSubscription
  );

  const getShouldOpenConfirmActivationModal = (toggleToActive: boolean) =>
    toggleToActive &&
    isBillingInfoProvided &&
    subscriptionStatus !== 'past_due' &&
    subscriptionStatus !== 'unpaid' &&
    subscriptionStatus !== 'active' &&
    !hasUnpaidInvoicesFromPreviousSubscription;

  const handleEditOffer = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      dispatch(
        navigationSlice.actions.navigateTo({
          path: generatePath(AppRoute.EditInstituteOffer, { id: _id }),
        })
      );
    },
    [dispatch, _id]
  );

  const handleDeleteOffer = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      dispatch(instituteSlice.actions.tryRemoveOffer(_id));
    },
    [dispatch, _id]
  );

  const handleEnterOffer = useCallback(() => {
    dispatch(
      navigationSlice.actions.navigateTo({
        path: getOfferRoutePath(offer),
      })
    );
  }, [offer, dispatch]);

  const rating = useMemo(() => `${roundToXDecimals(averageRating)}/5 (${totalRatings})`, [
    averageRating,
    totalRatings,
  ]);

  const handleActiveChange = (isActive: boolean) => {
    if (!offer._id) {
      return;
    }

    if (getShouldOpenConfirmActivationModal(isActive)) {
      return dispatch(
        paymentSlice.actions.openConfirmPaymentStartModal({ offerIdPendingActivation: offer._id })
      );
    }

    dispatch(instituteSlice.actions.toggleInstituteOfferActive({ id: offer._id, isActive }));
  };

  const renderWarningIfNeeded = () =>
    isPublished &&
    areOffersInvisible && (
      <Tooltip
        title={translate(
          translationKeys.tutoringInstituteProfile.offersPage.warningOffersAreInactiveTooltip
        )}
      >
        <WarningStyled />
      </Tooltip>
    );

  return (
    <>
      <RowWrapper onClick={handleEnterOffer}>
        <PointerText fontWeight={500}>{tutoringSchool?.name || ''}</PointerText>
        <PointerText>{location?.place || ''}</PointerText>
        <PointerText>{location?.address || ''}</PointerText>
        <PointerText fontWeight={500}>{rating}</PointerText>
        <PointerText>{appearedOnSearch}</PointerText>
        <PointerText>{detailsDisplayed}</PointerText>
        <SwitcherWrapper onClick={(e) => e.stopPropagation()}>
          <Switch
            checkedChildren={
              <Text size={10} color={'white'}>
                {translate(translationKeys.tutoringInstituteProfile.offersPage.active)}
              </Text>
            }
            unCheckedChildren={
              <Text size={10} color={'white'} style={{ marginLeft: -3 }}>
                {translate(translationKeys.tutoringInstituteProfile.offersPage.inactive)}
              </Text>
            }
            disabled={isTogglingOffersDisabled}
            style={{
              color: colors.primary,
              width: 62,
            }}
            checked={isPublished}
            onChange={handleActiveChange}
          />
          {renderWarningIfNeeded()}
        </SwitcherWrapper>
        <a style={{ textAlign: 'center' }}>
          <img
            color={colors.grayText}
            height={24}
            width={24}
            src={EDIT}
            alt={'edit'}
            onClick={handleEditOffer}
            loading="lazy"
          />
        </a>
        <a style={{ textAlign: 'center' }}>
          <img
            color={colors.grayText}
            height={24}
            width={24}
            src={DELETE}
            alt={'delete'}
            onClick={handleDeleteOffer}
            style={{ alignSelf: 'center' }}
            loading="lazy"
          />
        </a>
      </RowWrapper>
    </>
  );
};
